/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import { hot } from 'react-hot-loader/root'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/loader.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import Loadable from 'react-loadable'
import { AnnualComparison } from 'components/annualComparison'
import { TempStripes } from 'components/tempStripes'
import { GlobalStripes } from 'components/globalStripes'
import { MonthlyAnomaly } from 'components/monthlyAnomaly'
import { GlobalCo2 } from 'components/globalCo2'
import FlipBook from 'components/FlipBook'
import SwissMap from 'components/MapDisplay/SwissMap'
import WorldMap from 'components/MapDisplay/WorldMap'

// TODO Set the correct article ID here
const ARTICLE_ID = '915905648254'
const isMobile = browserCheck.isMobile

// FETCH DATA
const SwissLoader = Loadable({
  loader: () => {
    try {
      const stationData = fetch(
        'https://ta-interaktiv.storage.googleapis.com/klima-dashboard/meteoschweiz/anomaly_timeseries.json'
      ).then(x => x.json())
      return Promise.all([stationData])
    } catch (e) {
      console.log('ERR', e)
    }
  },
  loading: () => (
    <div className='ui text container load-wrapper'>
      <div className='ui segment'>
        <div className='ui active dimmer'>
          <div className='ui text loader'>Chargement des données…</div>
        </div>
      </div>
    </div>
  ),
  render([stationData]) {
    return (
      <>
        <div className='column'>
          <AnnualComparison
            data={stationData} // data={require('../../data/klima_dashboard_meteoschweiz_sma.json')}
            lastMonth='2022-06'
            title='Evolution des températures par rapport aux années précédentes'
            subtitle='Températures mensuelles moyennes en degrés Celsius en Suisse depuis 1864, période de référence: 1991-2020'
            lineColors={{
              current: '#d5211e',
              avg: '#FFFFFF',
              highlight: '#f5db5e',
              past: '#FFFFFF'
            }}
          />
        </div>

        <div className='column'>
          <TempStripes
            data={stationData} // data={require('../../data/klima_dashboard_meteoschweiz_sma.json')}
            lastMonth='2022-06'
            title='Dès le début des années 1980, la Suisse s’est réchauffée'
            subtitle='Écart par rapport à la norme de la température moyenne mensuelle en Suisse depuis 1864, période de référence: 1991-2020'
          />
        </div>
      </>
    )
  }
})

const GlobalLoader = Loadable({
  loader: () => {
    try {
      const stationData = fetch(
        'https://storage.googleapis.com/ta-interaktiv/klima-dashboard/noaa/anomaly_timeseries.json' //  'https://interaktiv.tagesanzeiger.ch/2020/daten-zum-klimawandel-in-der-schweiz/monthly_temperature_era5.json'
      ).then(x => x.json())
      return Promise.all([stationData])
    } catch (e) {
      console.log('ERR', e)
    }
  },
  loading: () => (
    <div className='ui text container load-wrapper'>
      <div className='ui segment'>
        <div className='ui active dimmer'>
          <div className='ui text loader'>Chargement des données…</div>
        </div>
      </div>
    </div>
  ),
  render([stationData], props) {
    return (
      <>
        <div className='column'>
          <MonthlyAnomaly
            data={stationData} // data={require('../../data/monthly_temperature_era5.json')}
            lastMonth='2022-06'
            title='Écart de température par rapport à la moyenne pluriannuelle'
            subtitle='Anomalies mensuelles moyennes de la température de l’air à la surface du globe, période de référence: 1901-2000'
            lineColors={{
              current: '#d5211e',
              avg: '#FFFFFF',
              highlight: '#f5db5e',
              past: '#FFFFFF'
            }}
          />
        </div>

        <div className='column'>
          <GlobalStripes
            data={stationData} // data={require('../../data/monthly_temperature_era5.json')}
            lastMonth='2022-06'
            title='La planète se réchauffe depuis le début des années 1980'
            subtitle='Déviation de la moyenne mensuelle de la température globale (surface de la terre et de la mer) depuis 1880, période de référence: 1901-2000'
          />
        </div>
      </>
    )
  }
})

export class AppComponent extends React.Component {
  constructor(props) {
    super(props)
    this.state = {}
  }

  render() {
    return (
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} inverted />
        <ThemeSwitch />

        <header className='ui vertical  very fitted inverted segment'>
          <div className='ui container'>
            <div className='ui header'>
              <h1>Le deuxième mois de juin le plus chaud en Suisse</h1>
              <h2>
                Seule la canicule historique 2003 a causé des températures
                supérieures en juin. Dans le monde, les régions qui ont
                enregistré des températures inédites couvrent 7,68% de la
                surface du globe, un record. Retrouvez nos cartes et graphiques
                sur l’évolution du climat.
              </h2>
            </div>
          </div>
        </header>

        <div className='ui vertical inverted segment'>
          <div className='ui text container'>
            <div className='ta metainfo'>
              <div className='ta author'>
                <nobr>Mathias Lutz,</nobr> <nobr>Andreas Moor,</nobr>{' '}
                <nobr>Marc Brupbacher</nobr>, Paul Ronga
              </div>
              <div className='ta updated'>Actualisé le 16 août 2022</div>
            </div>
            <div className='ta text' style={{ marginTop: '3em' }}>
              <div className='icon-wrapper'>
                <img src={require('./images/swiss.svg')} />
              </div>
              <h2>Juin 2022 en Suisse</h2>
              <p>
                La Suisse a enregistré le deuxième mois de juin le plus chaud
                depuis le début des mesures. La température moyenne sur
                l'ensemble du pays a été de 2,7 °C supérieure à la norme
                1991-2020. Seul le mois de juin 2003 a été encore plus chaud,
                avec 4,7 °C au-dessus de la norme.
              </p>
              <p>
                Le mois de mai 2022 avait également été le deuxième plus chaud,
                après mai 1868, depuis le début de la série de mesures de
                MétéoSuisse en 1864. En revanche, les températures des quatre
                premiers mois de l’année n’ont pas été exceptionnelles.
              </p>
              <p>
                Conjuguées au manque de pluie, les températures extrêmes ont
                causé une sécheresse exceptionnelle. Du 1er mai au 14 août, «il
                n'est jamais tombé aussi peu de pluie en Suisse romande que
                durant les 140 dernières années», indique MétéoSuisse{' '}
                <a
                  target='_blank'
                  rel='noreferrer'
                  href='https://www.meteosuisse.admin.ch/home/actualite/meteosuisse-blog.subpage.html/fr/data/blogs/2022/8/extreme-faiblesse-des-precipitations.html'
                >
                  sur son blog
                </a>
                .
              </p>
              <p>
                Il faudra plusieurs mois riches en précipitations pour combler
                le manque d’eau actuel.
              </p>

              <div className='source'>Source: MétéoSuisse</div>
            </div>
          </div>

          <div className='ui container'>
            <div className='ui equal width grid stackable'>
              <div className='equal width row'>
                <SwissLoader />
              </div>
              <div className='equal width row'>
                <div className='column'>
                  <h3>Température moyenne mensuelle depuis 1961</h3>
                  <h5>
                    Écart de la température moyenne mensuelle par rapport à la
                    norme 1981-2010, grille de 1 km
                  </h5>
                  <div style={{ marginTop: '1em' }}>
                    <FlipBook
                      render={(width, progress, setProgress, started) => (
                        <SwissMap
                          mode='tanom'
                          width={width}
                          progress={progress}
                          setProgress={setProgress}
                          started={started}
                          endDate={{
                            month: 6,
                            year: 2022
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className='column'>
                  <h3>Précipitations mensuelles depuis 1961</h3>
                  <h5>
                    Précipitations en pourcentage des précipitations de la norme
                    1981–2010, grille de 1 km
                  </h5>
                  <div style={{ marginTop: '1em' }}>
                    <FlipBook
                      render={(width, progress, setProgress, started) => (
                        <SwissMap
                          mode='ranom'
                          width={width}
                          progress={progress}
                          setProgress={setProgress}
                          started={started}
                          endDate={{
                            month: 6,
                            year: 2022
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='ui vertical inverted segment dark'>
          <div className='ui text container'>
            <div className='ta text'>
              <div className='icon-wrapper'>
                <img src={require('./images/global.svg')} />
              </div>
              <h2>Le mois de juin dans le monde</h2>
              <p>
                Les régions qui ont enregistré des températures records en juin
                2022 couvrent environ 7,68% de la surface du globe, ce qui
                dépasse le précédent record de 2010 (7,37%).
              </p>
              <p>
                La température mondiale à la surface des terres et des océans a
                été supérieure de 0,87 °C à la moyenne à long terme. Il s'agit,
                selon l'Agence américaine d'observation océanique et
                atmosphérique (NOAA), de la sixième valeur la plus élevée pour
                un mois de juin depuis le début des relevés mondiaux en 1880.
              </p>
              <p>
                Comme la Suisse, l’Europe dans son ensemble a connu cette année
                son deuxième mois de juin le plus chaud, précise la NOAA dans
                son{' '}
                <a
                  href='https://www.ncei.noaa.gov/access/monitoring/monthly-report/global/202206'
                  target='_blank'
                  rel='noreferrer'
                >
                  rapport sur le mois de juin
                </a>{' '}
                dans le monde.
              </p>
              {/* https://yaleclimateconnections.org/2022/07/nasa-june-2022-tied-as-earths-warmest-june-on-record/ */}
              <p>
                Selon le calcul de la NASA, le mois de juin 2022 a été le plus
                chaud à égalité avec juin 2020, depuis le début des mesures
                globales en 1880. La différence s’explique par la manière de
                traiter des régions pauvres en données, telles que l'Arctique.
                Les six mois de juin les plus chauds ont un écart d’à peine 0,08
                °C, ce qui explique qu’une petite différence de calcul ait un
                gros impact sur le classement. Les dix mois de juin les plus
                chauds sont tous survenus depuis 2010.
              </p>
              <p>
                La sécheresse affecte désormais de nombreuses régions du monde,
                notamment les Etats-Unis, la France et la Grande-Bretagne.
              </p>
              <div className='source'>
                Source:{' '}
                <a
                  target='_blank'
                  rel='noreferrer'
                  href='https://www.ncei.noaa.gov/access/monitoring/monthly-report/global/202206'
                >
                  NOAA Global Climate Report
                </a>
              </div>
            </div>
          </div>

          <div className='ui container'>
            <div className='ui equal width grid stackable'>
              <div className='equal width row'>
                <GlobalLoader />
              </div>
              <div className='equal width row'>
                <div className='column'>
                  <h3>
                    Écart de température par rapport à la moyenne à long terme
                  </h3>
                  <h5>Période de référence: 1981–2010</h5>
                  <div style={{ marginTop: '1em' }}>
                    <FlipBook
                      render={(width, progress, setProgress, started) => (
                        <WorldMap
                          width={width}
                          progress={progress}
                          setProgress={setProgress}
                          started={started}
                          endDate={{
                            month: 6,
                            year: 2022
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className='equal width row'>
                <div className='column'>
                  <GlobalCo2
                    data={require('../../data/co2-concentration-long-term.json')}
                    lastMonth='2022-06'
                    title='Concentration de CO₂ dans l’atmosphère'
                    subtitle='Concentration moyenne mondiale de dioxyde de carbone (CO₂) mesurée en parties par million (ppm). Mesurée avant 1958 à partir d’échantillons d’air préservés provenant de carottes de glace, puis de la station de mesure de Mauna Loa.'
                    lineColors={{
                      current: '#d5211e',
                      avg: '#FFFFFF',
                      highlight: '#f5db5e',
                      past: '#FFFFFF'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer
          inverted
          locale='fr'
          title='Comment évaluez-vous cette contribution?'
          hidePortfolioTeaser
          mailTo='paul.ronga@tamedia.ch'
          articleId={ARTICLE_ID}
          questions={[
            'Les graphiques sont-ils compréhensibles?',
            'Comment trouvez-vous le design de la page?'
          ]}
          credits={[
            [
              'Concept et développement',
              '<nobr>Andreas Moor,</nobr> <nobr>Marc Brupbacher,</nobr> <nobr>Mathias Lutz</nobr>'
            ],
            ['Texte et adaptation', 'Paul Ronga'],
            [
              'Inspiration',
              '<a href="https://www.meteostats.ch/">meteostats.ch</a>'
            ]
          ]}
        />
      </div>
    )
  }
}

export default hot('AppComponent')
